/**
 * shared-sweeps-upcart.js
 *
 * Shared Sweeps inside the UpCart drawer:
 *   1. "card-min" entries banner next to each cart line price
 *   2. the same banner on upsell tiles
 *   3. cart-tier progress bar directly under the drawer header
 *   4. "Total Entries" bar above the checkout button
 *   5. confetti burst from a tier's segment when the cart reaches that tier
 *
 * Cart numbers (1, 3, 4) come from /cart?view=sweeps -> templates/cart.sweeps.liquid
 * -> snippets/ss-entries-total.liquid, the same Liquid every other surface uses, so
 * the drawer can't drift from the rest of the theme. Upsell tiles (2) aren't in the
 * cart yet, so they use /products/{handle}?view=sweeps with the same per-unit formula.
 *
 * Works whether UpCart renders into a shadow root or light DOM. Re-runs on every
 * UpCart re-render (MutationObserver, debounced); each injected element carries a
 * key, so re-runs only touch what actually changed.
 */
(() => {
  'use strict';

  const HOST_ID = 'upCart';
  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const CART_URL = `${ROOT}cart?view=sweeps`;

  const WAIT_MS = 60000;
  const POLL_MS = 250;
  const DEBOUNCE_MS = 120;
  const ANIM_MS = 600;
  const CONFETTI_DELAY_MS = 350; // let the fill sweep into the segment first
  const CONFETTI_MS = 2400;
  const CELEBRATED_KEY = 'ss-upcart-tiers-celebrated';

  const SEL = {
    header: '.upcart-public-header',
    item: 'li.upcart-public-cart-item',
    pricing: '.upcart-public-component-product-tile__product-pricing',
    link: 'a[href*="variant="]',
    qty: '.upcart-public-component-product-tile__quantity-selector input',
    price: '[data-upcart-chunk="cart-item-price"]',
    checkoutContainer: '.upcart-checkout-button-container',
    checkoutButton: '.upcart-checkout-button',
    upsell: '[data-upcart-upsell]',
    upsellPriceRow: '.upcart-public-component-upsell-tile__price-row',
    upsellPrice: '[data-upcart-chunk="upsell-new-price"]',
    banner: '[data-ss-upcart]',
    progress: '[data-ss-upcart-progress]',
    total: '[data-ss-upcart-total]',
  };

  const STYLE_ID = 'ss-upcart-styles';
  const STYLES = `
    /* ── Entries banner (card-min) ─────────────────────────────────────── */
    .ss-banner-wrapper { display: flex; min-width: 0; }
    .ss-banner {
      border: 1px solid var(--ss-text, #000000);
      border-radius: 1.1rem;
      overflow: hidden;
      display: flex;
      align-items: center;
      width: fit-content;
      max-width: 100%;
      min-width: 0;
      position: relative;
      z-index: 2;
      color: var(--ss-text, #000000);
      background-color: var(--ss-bg, rgba(0, 0, 0, 0.06));
      box-sizing: border-box;
    }
    .ss-banner-count {
      font-weight: bold;
      white-space: nowrap;
      overflow: hidden;
      min-width: 0;
      color: var(--ss-count-text, #000000);
    }
    .ss-banner-multiplier {
      background-color: var(--ss-badge-bg, #000000);
      color: var(--ss-badge-text, #fff);
      flex-shrink: 0;
    }
    .ss-banner-wrapper--card-min { margin-top: 2.5px; justify-content: flex-start; width: 100%; min-width: 0; }
    .ss-banner-wrapper--card-min .ss-banner {
      column-gap: 8px;
      overflow: hidden;
      border-radius: 1.1rem;
      border: none;
      box-shadow: inset 0 0 0 1px var(--ss-text, #000000);
      max-width: 100%;
      min-width: 0;
    }
    .ss-banner-wrapper--card-min .ss-banner-count {
      margin-left: 8px;
      font-size: 12px;
      min-width: 0;
      flex: 0 1 auto;
    }
    .ss-banner-wrapper--card-min .ss-banner-multiplier {
      padding: 0 10px;
      font-size: 12px;
      border-radius: 0 1.1rem 1.1rem 0;
      flex-shrink: 0;
      align-self: stretch;
      display: flex;
      align-items: center;
    }

    /* Inline next to the line price. */
    .upcart-public-component-product-tile__product-pricing {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      column-gap: 8px;
      row-gap: 4px;
    }
    .upcart-public-component-product-tile__price-group { flex-shrink: 0; white-space: nowrap; }
    .upcart-public-component-product-tile__product-pricing > .ss-banner-wrapper--card-min {
      width: auto;
      margin-top: 0;
      flex: 0 1 auto;
    }

    /* Inline next to the upsell price. */
    .upcart-public-component-upsell-tile__price-row {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      column-gap: 6px;
      row-gap: 4px;
    }
    .upcart-public-component-upsell-tile__price-row > .ss-banner-wrapper--card-min {
      width: auto;
      margin-top: 0;
      flex: 0 1 auto;
    }

    /* ── Cart-tier progress bar (under the header) ─────────────────────── */
    .ss-upcart-progress {
      --ssp-text: #000000;
      --ssp-border: #000000;
      --ssp-track: rgba(0, 0, 0, 0.06);
      --ssp-fill: #000000;
      --ssp-fill-text: #ffffff;
      box-sizing: border-box;
      padding: 12px 16px;
      border-bottom: var(--upcart-internal-header-border-bottom, 1px solid rgba(0, 0, 0, 0.1));
      color: var(--ssp-text);
    }
    .ss-upcart-progress *,
    .ss-upcart-progress *::before,
    .ss-upcart-progress *::after { box-sizing: border-box; }
    .ss-upcart-progress__msg {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      margin: 0 0 10px;
      font-size: 14px;
      font-weight: 600;
      line-height: 1.3;
      text-align: center;
    }
    .ss-upcart-progress__msg strong { font-weight: 800; }
    .ss-upcart-progress__icon { display: inline-flex; flex-shrink: 0; }
    .ss-upcart-progress__icon svg { display: block; width: 22px; height: 22px; }

    /* Two identical label rows: dark labels on the track, light labels on the
       fill layer. The fill is revealed with clip-path, so a label is always
       readable whether its segment is filled, empty or half-way. */
    .ss-upcart-progress__track {
      position: relative;
      overflow: hidden;
      border: 1px solid var(--ssp-border);
      border-radius: 10px;
      background: var(--ssp-track);
    }
    .ss-upcart-progress__row { display: flex; height: 100%; }
    .ss-upcart-progress__seg {
      flex: 1 1 0;
      min-width: 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 5px 4px;
      text-align: center;
      line-height: 1.2;
    }
    .ss-upcart-progress__seg + .ss-upcart-progress__seg { border-left: 1px solid var(--ssp-border); }
    .ss-upcart-progress__label {
      max-width: 100%;
      font-size: 12px;
      font-weight: 700;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .ss-upcart-progress__at { font-size: 10px; font-weight: 600; opacity: 0.75; }
    .ss-upcart-progress__fill {
      position: absolute;
      top: 0; right: 0; bottom: 0; left: 0;
      background: var(--ssp-fill);
      color: var(--ssp-fill-text);
      clip-path: inset(0 100% 0 0);
      transition: clip-path ${ANIM_MS}ms cubic-bezier(0.22, 0.61, 0.36, 1);
    }
    .ss-upcart-progress__fill .ss-upcart-progress__seg + .ss-upcart-progress__seg {
      border-left-color: rgba(255, 255, 255, 0.35);
      border-left-color: color-mix(in srgb, var(--ssp-fill-text) 35%, transparent);
    }

    /* ── Total Entries bar (above checkout): button's colors reversed ──── */
    .ss-upcart-total {
      box-sizing: border-box;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      min-height: 50px;
      margin: 0 0 8px;
      padding: 0 16px;
      background: #ffffff;
      color: var(--upcart-internal-global-button-background-color, #18464A);
      border: 1px solid var(--upcart-internal-global-button-background-color, #18464A);
      border-radius: var(--upcart-internal-global-button-border-radius, 0);
      font: inherit;
      line-height: 1.2;
      text-align: center;
    }
    .ss-upcart-total__value { font-variant-numeric: tabular-nums; }
    .ss-upcart-total__mult {
      display: inline-block;
      vertical-align: middle;
      margin-left: 0.15em;
      padding: 2px 7px;
      line-height: 1.3;
      border-radius: var(--upcart-internal-global-button-border-radius, 0);
      background: var(--upcart-internal-global-button-background-color, #18464A);
      color: #ffffff;
      font-size: 0.8em;
      font-weight: 700;
    }
    .ss-upcart-total__mult[hidden] { display: none; }

    @media (prefers-reduced-motion: reduce) {
      .ss-upcart-progress__fill { transition: none; }
    }
  `;
  const STYLES_HIDE_NATIVE = `.upcart-public-rewards { display: none !important; }`;

  const ICON = `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2 22l4.5-12.5 8 8L2 22z" fill="currentColor" fill-opacity="0.18"/>
    <path d="M5.5 8.5c1-1 1-2.5 0-3.5M9 5c1.2-.6 2.6-.3 3.4.8M15.5 3.2c.2 1 .9 1.8 1.9 2M19 8c1 .2 2-.2 2.6-1" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
    <circle cx="18.5" cy="13" r="1" fill="currentColor"/>
    <circle cx="21" cy="16.5" r="1" fill="currentColor"/>
    <circle cx="14" cy="18.5" r="1" fill="currentColor"/>
    <path d="M2 22l4.5-12.5 8 8L2 22z" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/>
  </svg>`;

  const config = readConfig();
  const productCache = new Map(); // handle -> Promise<data | null>
  let cartCache = { signature: null, data: null };
  let running = false;
  let rerun = false;
  let timer = null;
  let upcartRoot = null;
  let lastFill = null;
  let lastTotal = null;
  let warnedEndpoint = false;
  let celebrated = readCelebrated(); // tiers already celebrated this session (null = no baseline yet)
  let pendingTier = null;            // index of the tier waiting to be celebrated
  let barVisible = false;
  let visibilityObserver = null;

  /* ---------- config / data ---------- */

  function readConfig() {
    const defaults = {
      text: '#000000',
      countText: '#000000',
      bg: 'rgba(0, 0, 0, 0.06)',
      badgeBg: '#000000',
      badgeText: '#FFFFFF',
      totalLabel: 'Total Entries',
      msgNext: "You're [amount] away from [label]!",
      msgDone: "You've unlocked [label]!",
      hideNativeRewards: false,
      confetti: true,
      confettiColors: ['#184641', '#CDE0E0', '#AA482E', '#E9B44C'],
    };
    try {
      const el = document.getElementById('ss-upcart-config');
      return el ? Object.assign(defaults, JSON.parse(el.textContent)) : defaults;
    } catch (e) {
      return defaults;
    }
  }

  // Alternate templates come back as text/html; slice to the JSON object defensively.
  function parseJson(text) {
    if (!text) return null;
    const start = text.indexOf('{');
    const end = text.lastIndexOf('}');
    if (start === -1 || end <= start) return null;
    try {
      return JSON.parse(text.slice(start, end + 1));
    } catch (e) {
      return null;
    }
  }

  function getProductData(handle) {
    if (!productCache.has(handle)) {
      const url = `${ROOT}products/${encodeURIComponent(handle)}?view=sweeps`;
      const promise = fetch(url, { credentials: 'same-origin' })
        .then((res) => (res.ok ? res.text() : null))
        .then(parseJson)
        .catch(() => {
          productCache.delete(handle); // allow retry on next render
          return null;
        });
      productCache.set(handle, promise);
    }
    return productCache.get(handle);
  }

  async function getSweepsCart(signature) {
    if (cartCache.data && cartCache.signature === signature) return cartCache.data;
    let data = null;
    try {
      const res = await fetch(CART_URL, { credentials: 'same-origin', cache: 'no-store' });
      data = res.ok ? parseJson(await res.text()) : null;
    } catch (e) {
      data = null;
    }
    if (!data || !Array.isArray(data.items) || typeof data.total !== 'number') {
      if (!warnedEndpoint) {
        warnedEndpoint = true;
        console.warn('[SharedSweeps] /cart?view=sweeps did not return sweeps JSON. Is templates/cart.sweeps.liquid published?');
      }
      return null; // not cached, so the next render retries
    }
    cartCache = { signature, data };
    return data;
  }

  /* ---------- DOM reading ---------- */

  function getVariantId(li) {
    const link = li.querySelector(SEL.link);
    if (!link) return null;
    try {
      const id = new URL(link.getAttribute('href'), location.origin).searchParams.get('variant');
      return id ? Number(id) : null;
    } catch (e) {
      return null;
    }
  }

  function readLines(root) {
    return Array.from(root.querySelectorAll(SEL.item)).map((li) => ({
      li,
      variantId: getVariantId(li),
      qty: (li.querySelector(SEL.qty) || {}).value || '',
      priceText: ((li.querySelector(SEL.price) || {}).textContent || '').trim(),
    }));
  }

  // Lines + the checkout button text (it carries the subtotal, so discount codes
  // and gift changes that don't touch a line price still trigger a refetch).
  function cartSignature(root, lines) {
    const btn = root.querySelector(SEL.checkoutButton);
    const lineSig = lines.map((l) => `${l.variantId}:${l.qty}:${l.priceText}`).join('|');
    return `${lineSig}#${btn ? btn.textContent.trim() : ''}`;
  }

  // Match DOM lines to cart items: same index first, then first unused item with that variant.
  function matchItems(lines, items) {
    const used = new Set();
    return lines.map((line, i) => {
      if (items[i] && !used.has(i) && items[i].variant_id === line.variantId) {
        used.add(i);
        return items[i];
      }
      const j = items.findIndex((it, k) => !used.has(k) && it.variant_id === line.variantId);
      if (j === -1) return null;
      used.add(j);
      return items[j];
    });
  }

  /* ---------- helpers ---------- */

  const fmt = (n) => Number(n).toLocaleString('en-US');
  const fmtMult = (m) => String(Math.round(Number(m) * 100) / 100);
  const reduceMotion = () =>
    !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  function el(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  // Same rounding as ss-entries-total: nearest dollar, exactly .50 rounds down.
  function roundDollars(cents) {
    const whole = Math.floor(cents / 100);
    return cents % 100 > 50 ? whole + 1 : whole;
  }

  // "$1,234.56" / "1.234,56 €" / "$549" -> cents. A trailing separator + exactly
  // 2 digits is treated as decimals; anything else as whole currency units.
  function parseMoneyToCents(text) {
    const cleaned = String(text || '').replace(/[^\d.,]/g, '');
    if (!cleaned) return null;
    const match = cleaned.match(/^(.*?)[.,](\d{2})$/);
    if (match) {
      const whole = parseInt(match[1].replace(/[.,]/g, '') || '0', 10);
      return whole * 100 + parseInt(match[2], 10);
    }
    const whole = parseInt(cleaned.replace(/[.,]/g, ''), 10);
    return Number.isNaN(whole) ? null : whole * 100;
  }

  function tweenNumber(node, from, to) {
    const token = (node._ssTween = (node._ssTween || 0) + 1);
    if (from == null || from === to || reduceMotion()) {
      node.textContent = fmt(to);
      return;
    }
    let start = null;
    requestAnimationFrame(function step(ts) {
      if (node._ssTween !== token) return; // superseded by a newer update
      if (start === null) start = ts;
      const p = Math.min((ts - start) / ANIM_MS, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      node.textContent = fmt(Math.round(from + (to - from) * eased));
      if (p < 1) requestAnimationFrame(step);
    });
  }

  /* ---------- entries banners (lines + upsells) ---------- */

  function buildBanner(model, key) {
    const wrapper = el('div', 'ss-banner-wrapper ss-banner-wrapper--card-min');
    wrapper.setAttribute('data-ss-upcart', '');
    wrapper.dataset.ssKey = key;
    wrapper.style.setProperty('--ss-text', config.text);
    wrapper.style.setProperty('--ss-count-text', config.countText);
    wrapper.style.setProperty('--ss-bg', config.bg);
    wrapper.style.setProperty('--ss-badge-bg', config.badgeBg);
    wrapper.style.setProperty('--ss-badge-text', config.badgeText);

    const banner = el('div', 'ss-banner shared-sweeps-entries-banner');
    banner.dataset.multiplier = String(model.multiplier);

    const count = el(
      'div',
      'ss-banner-count shared-sweeps-entries-count',
      model.bonus > 0
        ? `Earn ${fmt(model.entries)} Entries + ${fmt(model.bonus)} Bonus`
        : `Earn ${fmt(model.entries)} Entries`
    );

    // aria-hidden: the count already states the entries; "1X" would be read as "one X".
    const badge = el('div', 'ss-banner-multiplier shared-sweeps-entries-multiplier', `${fmtMult(model.multiplier)}X`);
    badge.setAttribute('aria-hidden', 'true');

    banner.append(count, badge);
    wrapper.append(banner);
    return wrapper;
  }

  // Add, update or remove the banner inside `container`. model = { entries, bonus, multiplier } | null
  function applyBanner(container, anchor, model) {
    const existing = container.querySelector(SEL.banner);
    if (!anchor || !model || (model.entries <= 0 && model.bonus <= 0)) {
      if (existing) existing.remove(); // also covers $0 free-gift lines
      return;
    }
    const key = `${model.entries}|${model.bonus}|${model.multiplier}`;
    if (existing && existing.dataset.ssKey === key) return;
    const banner = buildBanner(model, key);
    if (existing) existing.replaceWith(banner);
    else anchor.appendChild(banner);
  }

  function renderLine(line, item) {
    const model =
      item && item.eligible
        ? { entries: Number(item.entries) || 0, bonus: Number(item.bonus) || 0, multiplier: Number(item.multiplier) || 1 }
        : null;
    applyBanner(line.li, line.li.querySelector(SEL.pricing), model);
  }

  function getUpsellHandle(tile) {
    const handle = tile.getAttribute('data-upcart-upsell');
    if (handle) return handle;
    const link = tile.querySelector('a[href*="/products/"]');
    const m = link && link.getAttribute('href').match(/\/products\/([^/?#]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  }

  // Upsells use the price UpCart displays (follows the selected variant and any
  // upsell discount), run through the same per-unit formula as ss-entries-total.
  async function renderUpsell(tile) {
    const handle = getUpsellHandle(tile);
    if (!handle) return;
    const priceEl = tile.querySelector(SEL.upsellPrice);
    const cents = priceEl ? parseMoneyToCents(priceEl.textContent) : null;
    const data = await getProductData(handle);
    let model = null;
    if (data && data.eligible && cents != null) {
      const multiplier = Number(data.multiplier) || 1;
      model = {
        entries: Math.floor(roundDollars(cents) * multiplier),
        bonus: Number(data.bonus) || 0,
        multiplier,
      };
    }
    applyBanner(tile, tile.querySelector(SEL.upsellPriceRow), model);
  }

  /* ---------- progress bar ---------- */

  // Piecewise fill: every tier gets an equal-width segment, and progress is
  // measured within the current tier (previous threshold -> this threshold).
  function buildTierModel(data) {
    const tiers = (data.tiers || [])
      .filter((t) => Number(t.threshold) > 0)
      .sort((a, b) => a.threshold - b.threshold);
    if (!tiers.length) return null;

    const subtotal = Number(data.subtotal) || 0;
    let prev = 0;
    let fill = 100;
    let next = null;
    for (let i = 0; i < tiers.length; i++) {
      const t = tiers[i];
      if (subtotal >= t.threshold) {
        prev = t.threshold;
        continue;
      }
      next = t;
      const span = t.threshold - prev;
      const frac = span > 0 ? Math.max(0, (subtotal - prev) / span) : 0;
      fill = ((i + frac) * 100) / tiers.length;
      break;
    }
    fill = Math.min(100, Math.max(0, fill));
    return { tiers, subtotal, fill, next, top: tiers[tiers.length - 1] };
  }

  function segRow(tiers) {
    const row = el('div', 'ss-upcart-progress__row');
    row.setAttribute('aria-hidden', 'true');
    tiers.forEach((t) => {
      const seg = el('span', 'ss-upcart-progress__seg');
      seg.append(
        el('span', 'ss-upcart-progress__label', t.label),
        el('span', 'ss-upcart-progress__at', t.threshold_display)
      );
      row.append(seg);
    });
    return row;
  }

  function buildProgress(tiers) {
    const bar = el('div', 'ss-upcart-progress');
    bar.setAttribute('data-ss-upcart-progress', '');
    bar.style.setProperty('--ssp-text', config.text);
    bar.style.setProperty('--ssp-border', config.text);
    bar.style.setProperty('--ssp-track', config.bg);
    bar.style.setProperty('--ssp-fill', config.badgeBg);
    bar.style.setProperty('--ssp-fill-text', config.badgeText);

    const msg = el('div', 'ss-upcart-progress__msg');
    msg.setAttribute('aria-live', 'polite');
    const icon = el('span', 'ss-upcart-progress__icon');
    icon.setAttribute('aria-hidden', 'true');
    icon.innerHTML = ICON;
    msg.append(icon, el('span', 'ss-upcart-progress__text'));

    const track = el('div', 'ss-upcart-progress__track');
    track.setAttribute('role', 'progressbar');
    track.setAttribute('aria-valuemin', '0');
    const fill = el('div', 'ss-upcart-progress__fill');
    fill.append(segRow(tiers));
    track.append(segRow(tiers), fill);

    bar.append(msg, track);
    return bar;
  }

  // Builds the message from a template, bolding [amount] and [label]. textContent only.
  function fillTemplate(tpl, amount, label) {
    const frag = document.createDocumentFragment();
    String(tpl)
      .split(/(\[amount\]|\[label\])/)
      .forEach((part) => {
        if (part === '[amount]') frag.append(el('strong', 'ss-upcart-progress__amount', amount));
        else if (part === '[label]') frag.append(el('strong', null, label));
        else if (part) frag.append(document.createTextNode(part));
      });
    return frag;
  }

  function setFill(fillEl, fill, isNew) {
    const clip = (f) => `inset(0 ${(100 - f).toFixed(2)}% 0 0)`;
    fillEl.dataset.ssFill = fill.toFixed(2);
    // A freshly inserted bar animates from where the last one was (or from 0).
    const from = lastFill == null ? 0 : lastFill;
    lastFill = fill;
    if (isNew && from !== fill && !reduceMotion()) {
      fillEl.style.transition = 'none';
      fillEl.style.setProperty('clip-path', clip(from));
      void fillEl.offsetWidth;
      fillEl.style.transition = '';
      requestAnimationFrame(() => fillEl.style.setProperty('clip-path', clip(fill)));
      return;
    }
    fillEl.style.setProperty('clip-path', clip(fill)); // in-place updates use the CSS transition
  }

  function renderProgress(root, data) {
    const header = root.querySelector(SEL.header);
    let bar = root.querySelector(SEL.progress);
    const model = data ? buildTierModel(data) : null;
    const hasItems = !!(data && data.item_count > 0);
    if (model && !hasItems) noteReached(0); // cart emptied: re-arm every tier
    if (!header || !model || !hasItems) {
      if (bar) {
        bar.remove();
        stopTrackingVisibility();
      }
      return;
    }

    let isNew = false;
    const struct = model.tiers.map((t) => `${t.threshold}:${t.label}:${t.threshold_display}`).join('|');
    if (!bar || bar.dataset.ssStruct !== struct) {
      if (bar) bar.remove();
      bar = buildProgress(model.tiers);
      bar.dataset.ssStruct = struct;
      isNew = true;
    }
    // Only move when out of place; a no-op move would still fire the observer.
    if (header.nextElementSibling !== bar) header.after(bar);
    if (isNew) trackVisibility(bar);

    const key = `${model.subtotal}`;
    if (!isNew && bar.dataset.ssKey === key) return;
    bar.dataset.ssKey = key;

    const text = bar.querySelector('.ss-upcart-progress__text');
    text.textContent = '';
    text.append(
      model.next
        ? fillTemplate(config.msgNext, model.next.away_display, model.next.label)
        : fillTemplate(config.msgDone, '', model.top.label)
    );

    const track = bar.querySelector('.ss-upcart-progress__track');
    track.setAttribute('aria-valuemax', String(Math.round(model.top.threshold / 100)));
    track.setAttribute('aria-valuenow', String(Math.round(Math.min(model.subtotal, model.top.threshold) / 100)));
    track.setAttribute('aria-valuetext', text.textContent);

    setFill(bar.querySelector('.ss-upcart-progress__fill'), model.fill, isNew);

    noteReached(model.tiers.filter((t) => model.subtotal >= t.threshold).length);
    flushCelebration(bar);
  }

  /* ---------- tier confetti ---------- */

  function readCelebrated() {
    try {
      const v = window.sessionStorage.getItem(CELEBRATED_KEY);
      return v == null ? null : Number(v) || 0;
    } catch (e) {
      return null;
    }
  }

  function writeCelebrated(n) {
    if (celebrated === n) return;
    celebrated = n;
    try {
      window.sessionStorage.setItem(CELEBRATED_KEY, String(n));
    } catch (e) {
      /* storage blocked: still works for this page */
    }
  }

  // Runs whenever the subtotal changes. The first sighting in a session sets a
  // silent baseline, so opening the drawer with tiers already unlocked doesn't
  // fire. Dropping below a tier lowers the baseline, so re-crossing fires again.
  // The count is kept per session, so a tier reached via a full-page add-to-cart
  // still celebrates the next time the drawer is seen.
  function noteReached(reached) {
    if (celebrated == null) {
      writeCelebrated(reached);
      pendingTier = null;
      return;
    }
    if (reached < celebrated) writeCelebrated(reached);
    pendingTier = reached > celebrated ? reached - 1 : null;
  }

  // Waits until the bar is actually on screen: the cart often updates while the
  // drawer is still closed or sliding in. Several tiers crossed at once get one
  // burst, from the highest one.
  function flushCelebration(bar) {
    if (pendingTier == null || !barVisible || !bar.isConnected) return;
    const index = pendingTier;
    pendingTier = null;
    writeCelebrated(index + 1);
    if (config.confetti === false || reduceMotion()) return;
    setTimeout(() => {
      if (!bar.isConnected) return;
      const segs = bar.querySelectorAll('.ss-upcart-progress__track > .ss-upcart-progress__row > .ss-upcart-progress__seg');
      const r = (segs[index] || bar).getBoundingClientRect();
      burstConfetti(r.left + r.width / 2, r.top + r.height / 2, index);
    }, CONFETTI_DELAY_MS);
  }

  function trackVisibility(bar) {
    stopTrackingVisibility();
    if (typeof IntersectionObserver === 'undefined') {
      barVisible = true;
      return;
    }
    visibilityObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];
        barVisible = entry.isIntersecting && entry.intersectionRatio >= 0.9;
        flushCelebration(bar);
      },
      { threshold: [0, 0.9] }
    );
    visibilityObserver.observe(bar);
  }

  function stopTrackingVisibility() {
    if (visibilityObserver) visibilityObserver.disconnect();
    visibilityObserver = null;
    barVisible = false;
  }

  // Lightweight canvas confetti on a fixed overlay above the drawer (on <body>,
  // so it also works when UpCart lives in a shadow root). Bigger tier, bigger burst.
  function burstConfetti(x, y, tierIndex) {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext && canvas.getContext('2d');
    if (!ctx) return;
    canvas.setAttribute('aria-hidden', 'true');
    canvas.width = Math.round(vw * dpr);
    canvas.height = Math.round(vh * dpr);
    canvas.style.cssText =
      'position:fixed;top:0;left:0;width:100vw;height:100vh;pointer-events:none;z-index:2147483647;';
    document.body.appendChild(canvas);
    ctx.scale(dpr, dpr);

    const colors =
      Array.isArray(config.confettiColors) && config.confettiColors.length
        ? config.confettiColors
        : [config.badgeBg, config.text];
    const GRAVITY = 900; // px/s²
    const DRAG = 2.6;    // 1/s: quick pop, then a slow flutter down
    const count = Math.min(160, 70 + tierIndex * 35);
    const parts = [];
    for (let i = 0; i < count; i++) {
      const angle = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 0.7; // up, ±63°
      const speed = 500 + Math.random() * 450;
      parts.push({
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        rot: Math.random() * Math.PI * 2,
        vr: (Math.random() - 0.5) * 12,
        flip: Math.random() * Math.PI * 2,
        vf: 6 + Math.random() * 8,
        w: 6 + Math.random() * 5,
        h: 3 + Math.random() * 4,
        round: Math.random() < 0.3,
        color: colors[i % colors.length],
      });
    }

    let start = null;
    let last = null;
    function frame(ts) {
      if (start === null) start = last = ts;
      const dt = Math.min((ts - last) / 1000, 0.05);
      last = ts;
      const elapsed = ts - start;
      const fadeFrom = CONFETTI_MS * 0.65;
      ctx.clearRect(0, 0, vw, vh);
      ctx.globalAlpha = elapsed < fadeFrom ? 1 : Math.max(0, 1 - (elapsed - fadeFrom) / (CONFETTI_MS - fadeFrom));

      for (const p of parts) {
        p.vx -= DRAG * p.vx * dt;
        p.vy += (GRAVITY - DRAG * p.vy) * dt;
        p.x += (p.vx + Math.sin(p.flip) * 30) * dt;
        p.y += p.vy * dt;
        p.rot += p.vr * dt;
        p.flip += p.vf * dt;
        if (p.y > vh + 20) continue;
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.scale(1, Math.cos(p.flip)); // paper flipping over
        ctx.fillStyle = p.color;
        if (p.round) {
          ctx.beginPath();
          ctx.arc(0, 0, p.h * 0.75, 0, Math.PI * 2);
          ctx.fill();
        } else {
          ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        }
        ctx.restore();
      }

      if (elapsed < CONFETTI_MS) requestAnimationFrame(frame);
      else canvas.remove();
    }
    requestAnimationFrame(frame);
  }

  /* ---------- Total Entries bar ---------- */

  // Inline content with real spaces (not flex gap), so screen readers and
  // copy/paste get "Total Entries 720" rather than "Total Entries720".
  function buildTotal() {
    const box = el('div', 'ss-upcart-total');
    box.setAttribute('data-ss-upcart-total', '');
    const inner = el('span', 'ss-upcart-total__inner');
    const sep = el('span', 'ss-upcart-total__sep', '•');
    sep.setAttribute('aria-hidden', 'true');
    const mult = el('span', 'ss-upcart-total__mult');
    mult.hidden = true;
    inner.append(
      el('span', 'ss-upcart-total__label', config.totalLabel), ' ',
      sep, ' ',
      el('span', 'ss-upcart-total__value'), ' ',
      mult
    );
    box.append(inner);
    return box;
  }

  // UpCart's checkout typography sits on hashed CSS-module classes, so copy the
  // computed values instead of guessing selectors. Retries until the button is visible.
  function matchCheckoutTypography(box, btn) {
    if (box.dataset.ssTypo || !btn || !btn.offsetHeight) return;
    const cs = getComputedStyle(btn);
    ['font-family', 'font-size', 'font-weight', 'letter-spacing', 'text-transform'].forEach((prop) =>
      box.style.setProperty(prop, cs.getPropertyValue(prop))
    );
    box.style.minHeight = `${btn.offsetHeight}px`;
    box.dataset.ssTypo = '1';
  }

  function renderTotal(root, data) {
    const container = root.querySelector(SEL.checkoutContainer);
    let box = root.querySelector(SEL.total);
    if (!container || !data || !(data.total > 0)) {
      if (box) box.remove();
      return;
    }

    // Sit above the checkout button's wrapper so it spans the footer like the button does.
    const wrap = container.parentElement;
    const anchor = wrap && !wrap.classList.contains('upcart-footer') ? wrap : container;
    if (!box) box = buildTotal();
    if (box.nextElementSibling !== anchor) anchor.before(box);

    matchCheckoutTypography(box, container.querySelector(SEL.checkoutButton));

    const tierMult = Number(data.tier_multiplier) || 1;
    const key = `${data.total}|${tierMult}`;
    if (box.dataset.ssKey === key) return;
    box.dataset.ssKey = key;

    tweenNumber(box.querySelector('.ss-upcart-total__value'), lastTotal, data.total);
    lastTotal = data.total;

    const mult = box.querySelector('.ss-upcart-total__mult');
    mult.textContent = `${fmtMult(tierMult)}X`;
    mult.hidden = tierMult <= 1;
  }

  /* ---------- styles ---------- */

  // Shadow-root UpCart: styles go inside the shadow root. Light DOM: once in <head>.
  function ensureStyles(root) {
    const isShadow = typeof ShadowRoot !== 'undefined' && root instanceof ShadowRoot;
    const scope = isShadow ? root : document;
    if (scope.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = STYLES + (config.hideNativeRewards ? STYLES_HIDE_NATIVE : '');
    (isShadow ? root : document.head).appendChild(style);
  }

  /* ---------- main loop ---------- */

  async function run(root) {
    if (running) {
      rerun = true;
      return;
    }
    running = true;
    try {
      ensureStyles(root);

      const tasks = Array.from(root.querySelectorAll(SEL.upsell)).map(renderUpsell);

      const lines = readLines(root);
      const hasDrawer = lines.length || root.querySelector(SEL.header) || root.querySelector(SEL.checkoutContainer);
      if (hasDrawer) {
        const data = await getSweepsCart(cartSignature(root, lines));
        const items = matchItems(lines, (data && data.items) || []);
        lines.forEach((line, i) => renderLine(line, items[i]));
        renderProgress(root, data);
        renderTotal(root, data);
      }

      await Promise.all(tasks);
    } catch (err) {
      console.warn('[SharedSweeps] UpCart injection failed:', err);
    } finally {
      running = false;
      if (rerun) {
        rerun = false;
        schedule(root);
      }
    }
  }

  function schedule(root) {
    clearTimeout(timer);
    timer = setTimeout(() => run(root), DEBOUNCE_MS);
  }

  /* ---------- boot ---------- */

  // Resolves with the node UpCart renders into: #upCart.shadowRoot when UpCart
  // uses a shadow root, otherwise #upCart itself once #UpcartPopup is inside it.
  function waitForUpcartRoot() {
    return new Promise((resolve, reject) => {
      const started = Date.now();
      const check = () => {
        const host = document.getElementById(HOST_ID);
        if (host && host.shadowRoot) return resolve(host.shadowRoot);
        if (host && host.querySelector('#UpcartPopup')) return resolve(host);
        if (Date.now() - started > WAIT_MS) {
          return reject(new Error('UpCart drawer not found in #upCart (missing or closed shadow root).'));
        }
        setTimeout(check, POLL_MS);
      };
      check();
    });
  }

  function observe(root) {
    // Our own insertions also trigger this, but run() is a no-op when keys match.
    const observer = new MutationObserver(() => schedule(root));
    observer.observe(root, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['value'],
    });
    schedule(root);
  }

  waitForUpcartRoot()
    .then((root) => {
      upcartRoot = root;
      observe(root);
    })
    .catch((err) => console.warn('[SharedSweeps]', err.message));

  // Manual hook, e.g. after a custom cart update: window.SharedSweepsUpcart.refresh()
  window.SharedSweepsUpcart = {
    refresh() {
      cartCache.signature = null;
      if (upcartRoot) schedule(upcartRoot);
    },
  };
})();